import { ComponentType, memo } from 'react';

import {
  ManagePerspectivesFactory,
  WidgetsPerspective,
} from '~/components/MyWorkspace/ManagePerspectives';
import { RoleGroups } from '~/services/Permissions';

import {
  AnnouncementsWidget,
  ApprovalWidget,
  MyAbsencesWidget,
  MyCompensationWidget,
  MyDataWidget,
  MyNotificationsWidget,
  MyPerformanceWidget,
  MyTasksWidget,
  MyTeamAbsencesWidget,
  MyTeamWidget,
  QuickLinksWidget,
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // SolaIntelWidget,
  TeamAbsencesTodayWidget,
  TeamAnniversariesWidget,
  TeamCompensationWidget,
  TeamPerformanceWidget,
  TeamTasksWidget,
  TeamTimeManagementWidget,
  TeamTrainingsWidget,
} from '../';

import { MyWorkspaceWidgetNames, WidgetProps } from './widgets';

export type WidgetsMap = {
  [key in MyWorkspaceWidgetNames]: {
    title: string;
    component: ComponentType<WidgetProps<object>>;
    isVisible: WidgetsRolesVisibility;
    config?: {
      defaultData?: Partial<{ [key in RoleGroups]: object }>;
      manageComponent: ComponentType<{
        values?: object;
        setValue: (prop: string, value?: unknown) => void;
      }>;
    };
  };
};

export type WidgetsRolesVisibility = {
  [key in RoleGroups]: boolean;
};

export const myWorkspaceWidgetsMap: WidgetsMap = {
  [MyWorkspaceWidgetNames.AnnouncementsWidget]: {
    title: 'announcements',
    component: memo(AnnouncementsWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.MyTeamWidget]: {
    title: 'myTeam',
    component: memo(MyTeamWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.ApprovalWidget]: {
    title: 'Approval Widget',
    component: memo(ApprovalWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
  },
  [MyWorkspaceWidgetNames.TeamAbsencesTodayWidget]: {
    title: 'teamAbsencesToday',
    component: memo(TeamAbsencesTodayWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.TeamCompensationWidget]: {
    title: 'teamCompensation',
    component: memo(TeamCompensationWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
  },
  [MyWorkspaceWidgetNames.TeamPerformanceWidget]: {
    title: 'teamPerformance',
    component: memo(TeamPerformanceWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory([WidgetsPerspective.EMPLOYEE]),
    },
  },
  [MyWorkspaceWidgetNames.MyTeamAbsencesWidget]: {
    title: 'myTeamAbsences',
    component: memo(MyTeamAbsencesWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.TeamTimeManagementWidget]: {
    title: 'teamTimeManagement',
    component: memo(TeamTimeManagementWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.TeamTrainingsWidget]: {
    title: 'teamTrainings',
    component: memo(TeamTrainingsWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.TeamTasksWidget]: {
    title: 'teamTasks',
    component: memo(TeamTasksWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.TeamAnniversariesWidget]: {
    title: 'teamAnniversaries',
    component: memo(TeamAnniversariesWidget),
    isVisible: {
      [RoleGroups.HR]: true,
      [RoleGroups.STRAIGHT_MANAGER]: true,
      [RoleGroups.RESPONSIBLE_MANAGER]: true,
      [RoleGroups.EMPLOYEE]: false,
    },
    config: {
      defaultData: {
        [RoleGroups.EMPLOYEE]: {
          perspectives: [WidgetsPerspective.EMPLOYEE],
        },
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory(),
    },
  },
  [MyWorkspaceWidgetNames.MyDataWidget]: {
    title: 'myData',
    component: memo(MyDataWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.MyAbsencesWidget]: {
    title: 'myAbsences',
    component: memo(MyAbsencesWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.MyCompensationWidget]: {
    title: 'myCompensation',
    component: memo(MyCompensationWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
    config: {
      defaultData: {
        [RoleGroups.RESPONSIBLE_MANAGER]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_MANAGER],
        },
        [RoleGroups.STRAIGHT_MANAGER]: {
          perspectives: [WidgetsPerspective.MANAGER],
        },
        [RoleGroups.HR]: {
          perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
        },
      },
      manageComponent: ManagePerspectivesFactory([WidgetsPerspective.EMPLOYEE]),
    },
  },
  [MyWorkspaceWidgetNames.MyPerformanceWidget]: {
    title: 'myPerformance',
    component: memo(MyPerformanceWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.MyNotificationsWidget]: {
    title: 'myNotifications',
    component: memo(MyNotificationsWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.MyTasksWidget]: {
    title: 'myTasks',
    component: memo(MyTasksWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  [MyWorkspaceWidgetNames.QuickLinksWidget]: {
    title: 'quickLinks',
    component: memo(QuickLinksWidget),
    isVisible: {
      [RoleGroups.HR]: false,
      [RoleGroups.STRAIGHT_MANAGER]: false,
      [RoleGroups.RESPONSIBLE_MANAGER]: false,
      [RoleGroups.EMPLOYEE]: true,
    },
  },
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // [MyWorkspaceWidgetNames.SolaIntelWidget]: {
  //   title: 'solaIntel',
  //   component: memo(SolaIntelWidget),
  //   isVisible: {
  //     [RoleGroups.HR]: false,
  //     [RoleGroups.STRAIGHT_MANAGER]: false,
  //     [RoleGroups.RESPONSIBLE_MANAGER]: false,
  //     [RoleGroups.EMPLOYEE]: false,
  //   },
  //   config: {
  //     defaultData: {
  //       [RoleGroups.STRAIGHT_MANAGER]: {
  //         perspectives: [WidgetsPerspective.MANAGER],
  //       },
  //       [RoleGroups.HR]: {
  //         perspectives: [WidgetsPerspective.RESPONSIBLE_HR],
  //       },
  //     },
  //     manageComponent: ManagePerspectivesFactory([
  //       WidgetsPerspective.EMPLOYEE,
  //       WidgetsPerspective.RESPONSIBLE_MANAGER,
  //     ]),
  //   },
  // },
};

export const getWidgetTranslationKey = (widgetName: MyWorkspaceWidgetNames) => {
  return myWorkspaceWidgetsMap[widgetName].title || 'Widget name missing';
};
